import {
  type CommentActionEventArgs,
  type DocumentEditor,
  DocumentEditorContainerComponent,
  type RequestNavigateEventArgs,
  Toolbar,
  type ToolbarItem,
} from "@syncfusion/ej2-react-documenteditor";
import { memo, type RefObject, useEffect } from "react";
import { wordProcessorHost } from "../api/config";
import { scrollToLinkedParagraph } from "../helpers/documentActions";
import { saveDocument, useDebounce } from "../api/utils";
import { DocumentVersionStatus } from "~/config/types";
import { enforceDocumentProtection } from "../helpers/permissions";
import { useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";

DocumentEditorContainerComponent.Inject(Toolbar);

const localeMap = (locale?: string) => {
  switch (locale) {
    case "de":
      return "de";
    case "en-US":
      return "en-US";
    case "sv":
      return "sv";
    default:
      return undefined;
  }
};

interface Props {
  containerRef: RefObject<DocumentEditorContainerComponent | null>;
  documentId: number;
  documentContent?: string;
  fullName: string;
  locale?: string;
  documentStatus: DocumentVersionStatus;
  isActiveUser: boolean;
  isReadOnlyStatus: boolean;
  setHasUnsavedChanges: (value: boolean) => void;
}

const Editor = memo(
  ({
    documentId,
    containerRef,
    documentContent,
    fullName,
    locale,
    documentStatus,
    isActiveUser,
    isReadOnlyStatus,
    setHasUnsavedChanges,
  }: Props) => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();

    useEffect(() => {
      if (containerRef.current) {
        const { documentEditor } = containerRef.current;
        void enforceDocumentProtection(
          documentEditor,
          documentId,
          isReadOnlyStatus,
          isActiveUser,
        );
      }
    }, [isReadOnlyStatus, isActiveUser, containerRef, documentId]);

    const callbackRef = (
      container: DocumentEditorContainerComponent | null,
    ) => {
      if (container) {
        const { documentEditor } = container;

        documentEditor.isReadOnly = true;
        documentEditor.showRevisions = true;

        if (documentContent?.length) documentEditor.open(documentContent);

        documentEditor.contextMenu.addCustomMenu(
          documentStatus === DocumentVersionStatus.published
            ? [
                {
                  text: t("commons.buttons.copySelectionLink"),
                  id: "copy_link_to_paragraph",
                  iconCss: "e-icons e-link",
                },
              ]
            : [],
        );
        documentEditor.requestNavigate = handleRequestNavigate;
        documentEditor.documentChange = handleContentChange;

        documentEditor.showRestrictEditingPane(false);
        scrollToLinkedParagraph(documentEditor, searchParams);
        containerRef.current = container;
      }
    };

    const handleRequestNavigate = ({
      linkType,
      navigationLink,
    }: RequestNavigateEventArgs) => {
      // Next line is fix for https://north-house.atlassian.net/browse/NH-157
      const trimmedNavigationLink = navigationLink.replace(/["]+/g, "");

      if (linkType !== "Bookmark") {
        window.open(trimmedNavigationLink, "_blank");
      }
    };

    const debounceAutoSave = useDebounce((de: DocumentEditor) => {
      void saveDocument(
        documentId,
        de,
        () => setHasUnsavedChanges(false),
        undefined,
      );
    }, 2000);

    const handleContentChange = () => {
      if (containerRef.current?.documentEditor) {
        setHasUnsavedChanges(true);
        debounceAutoSave(containerRef.current.documentEditor);
      }
    };

    const handleCommentAction = (args: CommentActionEventArgs) => {
      if (
        (args.type === "Delete" || args.type === "Edit") &&
        args.author !== containerRef.current?.documentEditor.currentUser
      ) {
        args.cancel = true;
        alert("Only comment or document author can edit or delete the comment");
      }
    };

    return (
      <DocumentEditorContainerComponent
        id="document-editor"
        ref={callbackRef}
        locale={localeMap(locale)}
        height="100%"
        serviceUrl={`${wordProcessorHost}/`}
        currentUser={fullName}
        contentChange={handleContentChange}
        beforeCommentAction={handleCommentAction}
        showPropertiesPane={false}
        enableToolbar={!isReadOnlyStatus && isActiveUser}
        enableComment={!isReadOnlyStatus && isActiveUser}
        toolbarItems={getToolbarItems(documentStatus, isActiveUser)}
      />
    );
  },
);

Editor.displayName = "Editor";

const defaultToolbarItems: ToolbarItem[] = [
  "Separator",
  "Undo",
  "Redo",
  "Separator",
  "Image",
  "Table",
  "Hyperlink",
  "Bookmark",
  "TableOfContents",
  "Separator",
  "Header",
  "Footer",
  "PageSetup",
  "PageNumber",
  "Break",
  "InsertFootnote",
  "InsertEndnote",
  "Separator",
  "Find",
  "Separator",
  "Comments",
  "TrackChanges",
  "Separator",
  "LocalClipboard",
  "Separator",
  "FormFields",
  "UpdateFields",
];

const getToolbarItems = (
  status: DocumentVersionStatus,
  isActiveUser: boolean,
): ToolbarItem[] => {
  if (status === DocumentVersionStatus.draft) {
    return isActiveUser
      ? ["Open", ...defaultToolbarItems]
      : defaultToolbarItems;
  }
  return [];
};

export default Editor;
